// Generated from src/collectorTemplate.ts.
/** Canonical physical card contract. Contexts may arrange cards, never rewrite their faces. */
export const COLLECTOR_TEMPLATE_VERSION = "bilt.collector.v2";
export const collectorMaterial = {
    ratio: 5 / 7,
    width: 280,
    height: 392,
    radius: 14,
    ink: "#F4F3EF",
    secondary: "#B1B4BA",
    stock: "#101215",
    recess: "#15181C",
    edge: "#303237",
    accent: "#FF9A43",
    bronze: "#A7774C",
    bronzeInk: "#FFE0BC",
    bronzeTop: "#423023",
    bronzeBottom: "#1A1918",
    redline: "#FF797C",
    certification: "#F4D26C",
    hitTarget: 44,
};
export const collectorTemplates = {
    vehicle: {
        front: ["modification_count", "cost", "power", "torque", "redline"],
        back: ["engine", "transmission", "drivetrain", "chassis"],
        heading: "Technical record",
    },
    event: {
        front: ["start_at", "venue", "city"],
        back: ["end_at", "timezone", "host", "category"],
        heading: "Schedule & organizer",
    },
    club: {
        front: ["region", "focus"],
        back: ["founded_year"],
        heading: "Community charter",
    },
    shop: {
        front: ["specialties", "city"],
        back: ["services", "address"],
        heading: "Service record",
    },
    place: {
        front: ["category", "city"],
        back: ["offering", "exhibition", "address", "operator", "access"],
        heading: "Visitor notes",
    },
    booth: {
        front: ["campaign_title", "zone"],
        back: ["exhibitor", "host", "category", "description"],
        heading: "Exhibitor sheet",
    },
    activation: {
        front: ["category", "result"],
        back: ["host", "session", "description"],
        heading: "Session record",
    },
    destination: {
        front: ["position", "campaign_title"],
        back: ["place_name", "description"],
        heading: "Route note",
    },
    checkpoint: {
        front: ["position", "campaign_title"],
        back: ["criterion", "optional"],
        heading: "Published criterion",
    },
    commemorative: {
        front: ["occasion", "issuer"],
        back: ["story", "credits"],
        heading: "Edition colophon",
    },
};
const labels = {
    modification_count: "Mods",
    cost: "Cost",
    power: "Power",
    torque: "Torque",
    redline: "RPM",
    founded_year: "Founded",
    place_name: "Linked place",
    start_at: "Starts",
    end_at: "Ends",
    campaign_title: "Program",
    optional: "Requirement",
    position: "Stop",
    drivetrain: "Drive",
};
/** Empty is distinct from zero. Objects, non-finite numbers and arbitrary values never become facts. */
export function collectorValue(value) {
    if (typeof value === "number")
        return Number.isFinite(value) ? String(value) : "-";
    if (typeof value !== "string")
        return "-";
    return value.trim() || "-";
}
export function collectorTemplate(kind) {
    return Object.prototype.hasOwnProperty.call(collectorTemplates, kind)
        ? collectorTemplates[kind]
        : null;
}
export function collectorFacts(kind, face, facts, complete = false) {
    const definition = collectorTemplate(kind);
    if (!definition)
        return [];
    return definition[face]
        .flatMap((key) => {
        const raw = facts?.[key];
        let value = collectorValue(raw);
        if (value === "-" && !(kind === "vehicle" && face === "front"))
            return [];
        if (value !== "-" && key === "power")
            value += ` ${facts?.power_unit === "WHP" ? "WHP" : "HP"}`;
        if (value !== "-" && key === "torque")
            value += " lb-ft";
        if (value !== "-" && key === "redline" && Number.isFinite(Number(raw)))
            value = Number(raw).toLocaleString("en-US");
        if (value !== "-" && key === "cost" && Number.isFinite(Number(raw))) {
            const n = Number(raw);
            value =
                n >= 1000
                    ? `$${Math.round(n / 1000)}K`
                    : `$${n.toLocaleString("en-US")}`;
        }
        return [
            {
                key,
                label: labels[key] ||
                    key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()),
                value,
            },
        ];
    })
        .slice(0, complete || face === "front" ? undefined : 4);
}
/** Preserve issue identity and explicit feature order; never merge awards solely by title. */
export function collectorRecognition(input, vehicleId) {
    if (!Array.isArray(input))
        return [];
    const seen = new Set();
    const awards = new Set();
    return input
        .filter((v) => !!v && typeof v === "object")
        .filter((v) => v.is_active !== false &&
        v.is_active !== 0 &&
        v.revoked_at == null &&
        (!vehicleId || !v.vehicle_id || Number(v.vehicle_id) === vehicleId))
        .slice()
        .sort((a, b) => {
        const position = (v) => typeof v.featured_position === "number"
            ? v.featured_position
            : Number.MAX_SAFE_INTEGER;
        const rank = (v) => {
            const design = v.design;
            return [
                "major",
                "award",
                "participation",
                "affiliation",
                "milestone",
            ].indexOf(String(design?.prestige_tier));
        };
        const prestige = (v) => rank(v) < 0 ? 99 : rank(v);
        return (position(a) - position(b) ||
            prestige(a) - prestige(b) ||
            String(b.issued_at || "").localeCompare(String(a.issued_at || "")) ||
            String(a.public_id || a.id).localeCompare(String(b.public_id || b.id)));
    })
        .flatMap((v) => {
        const id = typeof v.public_id === "string"
            ? v.public_id
            : typeof v.id === "number"
                ? String(v.id)
                : "";
        if (!id || seen.has(id))
            return [];
        const award = typeof v.award_id === "number" && v.award_id > 0 ? v.award_id : null;
        if (award && awards.has(award))
            return [];
        if (award)
            awards.add(award);
        seen.add(id);
        const d = v.design && typeof v.design === "object"
            ? v.design
            : {};
        const title = collectorValue(d.name || d.short_label || v.title);
        if (title === "-")
            return [];
        const issuer = v.issuer && typeof v.issuer === "object"
            ? v.issuer
            : {};
        const entity = (issuer.event || issuer.club);
        return [
            {
                id,
                title,
                category: collectorValue(d.badge_category),
                issuer: typeof entity?.title === "string"
                    ? entity.title
                    : typeof entity?.name === "string"
                        ? entity.name
                        : null,
                issuedAt: typeof v.issued_at === "string" ? v.issued_at : null,
                artwork: typeof d.custom_logo_path === "string" ? d.custom_logo_path : null,
                source: v,
            },
        ];
    });
}
export const collectorBoundaries = {
    vehicle: "",
    event: "A collectible occasion record. Not admission.",
    club: "Collection does not confer club membership.",
    shop: "A visit record does not prove a service or purchase.",
    place: "Visit evidence and program qualification are separate.",
    booth: "A booth visit does not grant marketing consent.",
    activation: "Results appear only when supplied by an approved source.",
    destination: "This route stop and its linked place keep separate identities.",
    checkpoint: "Qualification follows the published program criterion.",
    commemorative: "No physical visit is implied by this commemorative.",
};
/** Select content before platform rendering. Overflow remains in the separately readable record. */
export function collectorPresentation(kind, facts, badges, sourceStory) {
    const definition = collectorTemplate(kind);
    const recognition = collectorRecognition(badges);
    const frontRecognition = kind === "vehicle" ? recognition.slice(0, 5) : [];
    const reverseRecognition = recognition.slice(frontRecognition.length, frontRecognition.length + 2);
    const front = collectorFacts(kind, "front", facts);
    const back = collectorFacts(kind, "back", facts);
    const story = collectorValue(sourceStory);
    const reverseStory = reverseRecognition.length === 0 &&
        story !== "-" &&
        ![...front, ...back].some((f) => f.value === story)
        ? story
        : null;
    return {
        version: COLLECTOR_TEMPLATE_VERSION,
        heading: definition?.heading || "Collected record",
        front,
        back,
        frontRecognition,
        reverseRecognition,
        reverseStory,
        density: recognition.length || back.length > 2
            ? "rich"
            : back.length || front.some((f) => f.value !== "-")
                ? "standard"
                : "minimal",
        boundary: collectorBoundaries[kind] || "",
    };
}
/** Issued badge rail art: the same 64-unit roundel, source palette and short label on both platforms. */
export function collectorBadgeMark(design) {
    const color = (v, fallback) => typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v) ? v : fallback;
    const tier = design.prestige_tier;
    return {
        primary: color(design.primary_color, "#1A1A1A"),
        secondary: color(design.secondary_color, "#F97316"),
        accent: color(design.accent_color, "#FAFAFA"),
        label: String(design.short_label || design.name || "")
            .trim()
            .slice(0, 3),
        strokeWidth: tier === "major" ? 2.5 : tier === "award" ? 2 : 1.25,
        strokeOpacity: tier === "major" ? 0.95 : tier === "award" ? 0.8 : 0.55,
        size: 26,
    };
}
/** Stable SVG silhouettes — viewBox 0 0 64 64, centered content area. */
export function collectorBadgePath(shape) {
    switch (shape) {
        case "shield":
            return "M32 4 L56 12 V34 C56 48 44 56 32 60 C20 56 8 48 8 34 V12 Z";
        case "hexagon":
            return "M20 6 H44 L58 32 L44 58 H20 L6 32 Z";
        case "pennant":
            return "M8 8 H56 L48 56 H8 Z";
        case "crest":
            return "M32 4 L54 12 V30 C54 46 42 54 32 60 C22 54 10 46 10 30 V12 Z";
        case "plaque":
            return "M10 8 H54 L60 16 V48 L54 56 H10 L4 48 V16 Z";
        case "rounded_rectangle":
            return "M12 8 H52 A4 4 0 0 1 56 12 V52 A4 4 0 0 1 52 56 H12 A4 4 0 0 1 8 52 V12 A4 4 0 0 1 12 8 Z";
        case "circle":
        case "roundel":
        default:
            return "M32 4 A28 28 0 1 1 31.9 4 Z";
    }
}
/** Engraved reverse illustrations, shared by SVG and Skia. Decorative, never evidence or a score. */
export const collectorReverseArt = {
    vehicle: {
        caption: "Under the bodywork",
        path: "M24 18 Q24 10 32 10 H48 Q56 10 56 18 V62 Q56 70 48 70 H32 Q24 70 24 62 Z M28 26 H52 M28 54 H52 M40 26 V54 M32 34 H48 V46 H32 Z M18 20 H24 V32 H18 Z M56 20 H62 V32 H56 Z M18 50 H24 V62 H18 Z M56 50 H62 V62 H56 Z",
    },
    event: {
        caption: "Behind the occasion",
        path: "M14 20 H66 V66 H14 Z M14 32 H66 M26 14 V26 M54 14 V26 M24 42 H32 V50 H24 Z M38 42 H46 M52 42 H58 M24 58 H32 M38 58 H46 M52 58 H58",
    },
    club: {
        caption: "Roots & community",
        path: "M40 10 L64 20 V42 Q64 58 40 70 Q16 58 16 42 V20 Z M29 30 A5 5 0 1 0 29 40 A5 5 0 1 0 29 30 M51 30 A5 5 0 1 0 51 40 A5 5 0 1 0 51 30 M23 52 Q29 42 35 52 M45 52 Q51 42 57 52 M35 24 H45",
    },
    shop: {
        caption: "Inside the workshop",
        path: "M12 34 L40 14 L68 34 M18 30 V66 H62 V30 M27 66 V42 H53 V66 M27 50 H53 M27 58 H53 M35 28 H45",
    },
    place: {
        caption: "Notes from the place",
        path: "M14 22 L30 16 L50 24 L66 18 V62 L50 68 L30 60 L14 66 Z M30 16 V60 M50 44 V68 M50 14 C34 14 34 34 50 46 C66 34 66 14 50 14 Z M50 22 A5 5 0 1 0 50 32 A5 5 0 1 0 50 22",
    },
    booth: {
        caption: "Meet the exhibitor",
        path: "M12 18 H68 V32 H12 Z M18 32 V66 M62 32 V66 M12 66 H68 M24 48 H56 V66 H24 Z M32 38 H48 M28 18 V32 M52 18 V32",
    },
    activation: {
        caption: "Inside the experience",
        path: "M24 16 H56 L66 26 V54 L56 64 H24 L14 54 V26 Z M34 28 L52 40 L34 52 Z M8 34 V46 M72 34 V46",
    },
    destination: {
        caption: "Along the route",
        path: "M16 60 C16 38 64 52 64 30 M16 54 A6 6 0 1 0 16 66 A6 6 0 1 0 16 54 M64 12 C48 12 48 30 64 40 C80 30 80 12 64 12 Z M64 19 A4 4 0 1 0 64 27 A4 4 0 1 0 64 19 M12 22 L26 10 L42 28 M6 30 H42",
    },
    checkpoint: {
        caption: "At this checkpoint",
        path: "M22 68 V14 M22 16 Q34 8 44 16 Q54 24 66 16 V42 Q54 50 44 42 Q34 34 22 42 M12 68 H34 M44 16 V42 M22 29 Q34 21 44 29 Q54 37 66 29",
    },
    commemorative: {
        caption: "The story preserved",
        path: "M22 12 H58 V58 L50 68 H22 Z M58 58 H50 V68 M30 24 H50 M30 32 H50 M30 40 H44 M34 50 L37 56 L44 57 L39 62 L40 69 L34 66 L28 69 L29 62 L24 57 L31 56 Z",
    },
};
/** Highlight only explicitly supplied drive layouts; unknown layouts stay unfilled. */
export function collectorDriveHighlight(facts) {
    const drive = collectorValue(facts?.drivetrain).toUpperCase();
    const front = "M18 20 H24 V32 H18 Z M56 20 H62 V32 H56 Z";
    const rear = "M18 50 H24 V62 H18 Z M56 50 H62 V62 H56 Z";
    if (["AWD", "4WD", "4X4"].includes(drive))
        return `${front} ${rear}`;
    return drive === "FWD" ? front : drive === "RWD" ? rear : "";
}
/** A small, complete category distribution. Omitted/hidden data never becomes a zero. */
export function collectorModificationMap(input) {
    const counts = new Map();
    if (Array.isArray(input))
        for (const item of input) {
            if (!item || typeof item !== "object")
                continue;
            const category = typeof item.category === "string" ? item.category.trim() : "";
            if (!category || !Number.isSafeInteger(item.count) || item.count <= 0)
                continue;
            counts.set(category, (counts.get(category) || 0) + item.count);
        }
    const all = Array.from(counts)
        .map(([category, count]) => ({ category, count }))
        .sort((a, b) => b.count - a.count || a.category.localeCompare(b.category));
    const total = all.reduce((sum, row) => sum + row.count, 0);
    if (!Number.isSafeInteger(total))
        return { rows: [], total: 0, systems: 0 };
    const rows = all.length > 4
        ? [
            ...all.slice(0, 3),
            {
                category: `${all.length - 3} other systems`,
                count: all.slice(3).reduce((sum, row) => sum + row.count, 0),
            },
        ]
        : all;
    return { rows, total, systems: all.length };
}
